import { AgGridAngular } from '@ag-grid-community/angular';
import { CellClickedEvent, GridApi, GridOptions, GridReadyEvent } from '@ag-grid-community/core';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import {
  EntityType,
  EventType,
  getPrepaidsSummaryQuery,
  GqlService,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { decimalAdd } from '@shared/utils';
import { ExportType, Utils } from '@shared/utils/utils';

import { PrepaidsGridOptions } from './prepaids.constants';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular, TableSkeletonComponent, ExportExcelButtonComponent, StickyGridDirective],
  selector: 'aux-prepaids',
  templateUrl: './prepaids.component.html',
})
export class PrepaidsComponent {
  eventService = inject(EventService);
  gqlService = inject(GqlService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  router = inject(Router);

  gridData = signal<getPrepaidsSummaryQuery[]>([]);
  loadingGridData = signal<boolean>(false);

  btnLoading$ = new BehaviorSubject<'export' | false>(false);
  gridAPI!: GridApi;

  goToPrepaidDetailPage = (event: CellClickedEvent): void => {
    if (event.data?.vendor_id) {
      void this.router.navigateByUrl(
        `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.PREPAIDS}/${event.data?.vendor_id}`
      );
    }
  };

  gridOptions: GridOptions = PrepaidsGridOptions(this.goToPrepaidDetailPage);

  private calculatePinnedBottomData(): {
    long_term_amount: number;
    short_term_amount: number;
    total_prepaid_balance: number;
  } {
    const uniqVendorCurrencies = uniq(this.gridData().map((row) => row.vendor_currency));
    const columnsWithAggregation = [
      'total_prepaid_balance',
      'long_term_amount',
      'short_term_amount',
    ] as const;

    if (uniqVendorCurrencies.length > 1) {
      return {
        long_term_amount: 0,
        short_term_amount: 0,
        total_prepaid_balance: 0,
      };
    }

    return this.gridData().reduce(
      (acc, row) => {
        for (const key of columnsWithAggregation) {
          const currentVal = acc[key];
          const additionalVal = row[key];

          if (Utils.isNumber(currentVal) && Utils.isNumber(additionalVal)) {
            acc[key] = decimalAdd(currentVal, additionalVal);
          }
        }

        return acc;
      },
      {
        long_term_amount: 0,
        short_term_amount: 0,
        total_prepaid_balance: 0,
      }
    );
  }

  constructor() {
    this.onTrialChange();
  }

  onExport = async () => {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const dateStr = dayjs(new Date()).format('YYYY.MM.DD-HHmmss');

    if (this.btnLoading$.getValue()) {
      return;
    }

    this.btnLoading$.next('export');

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.PREPAID_SUMMARY,
          filename: `${trialName}_auxilius-prepaids_${dateStr}`,
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.btnLoading$.next(false);
  };

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    api.sizeColumnsToFit();
    this.setBottomData();
  }

  onTrialChange(): void {
    this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => {
          this.loadingGridData.set(true);

          return this.gqlService.getPrepaidsSummary$();
        })
      )
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        if (result.success) {
          this.gridData.set(result.data || []);
        }

        this.loadingGridData.set(false);
      });
  }

  private setBottomData(): void {
    const totals = this.calculatePinnedBottomData();

    if (
      totals.total_prepaid_balance === 0 &&
      totals.long_term_amount === 0 &&
      totals.short_term_amount === 0
    ) {
      this.gridAPI.setGridOption('pinnedBottomRowData', []);
    } else {
      this.gridAPI.setGridOption('pinnedBottomRowData', [
        {
          vendor_id: 'Total',
          ...totals,
        },
      ]);
    }
  }
}
